import { HubFeature } from '@ats/api/cm';
import { LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

type CPCertAgreementDetailLocNames =
	| 'CPCertAgreementDetailsPageTitle'
	| 'CPCertAgreementDetailsPageDescription'
	| 'CPCertAgreementDetailsButtonBack'
	| 'CPCertAgreementDetailsButtonCancel'
	| 'CPCertAgreementDetailsButtonEdit'
	| 'CPCertAgreementDetailsButtonInfo'
	| 'CPCertAgreementDetailsButtonUpdate'
	| 'CPCertAgreementDetailsFieldLabelActiveDate'
	| 'CPCertAgreementDetailsFieldLabelDataProvider'
	| 'CPCertAgreementDetailsFieldLabelDocumentation'
	| 'CPCertAgreementDetailsFieldLabelDocumentationLanguage'
	| 'CPCertAgreementDetailsFieldLabelExpireDate'
	| 'CPCertAgreementDetailsFieldLabelInvalid'
	| 'CPCertAgreementDetailsFieldLabelNotes'
	| 'CPCertAgreementDetailsFieldLabelProvider'
	| 'CPCertAgreementDetailsFieldLabelReviewState'
	| 'CPCertAgreementDetailsFieldLabelUpdated'
	| 'CPCertAgreementDetailsNotificationMessageUpdateSuccess'
	| 'CPCertAgreementDetailsFieldErrorDocumentationRequired'
	| 'CPCertAgreementDetailsFieldErrorDocumentationSize'
	| 'CPCertAgreementDetailsFieldErrorDocumentationLimit'
	| 'CPCertAgreementDetailsFieldErrorDocLanguageRequired'
	| 'CPCertAgreementDetailsFieldErrorDocLanguageInvalid'
	| 'CPCertAgreementDetailsFieldErrorProviderRequired';

export const cpCertAgreementDetailLocs: LocDefs<CPCertAgreementDetailLocNames> = {
	CPCertAgreementDetailsPageTitle: {
		defaultValue: 'Cert Agreement Details',
		description: 'Page title for: Cert Agreement Details.',
		displayPath: 'Credentials Agreement|Details|Title',
		portal: 'CP',
		displayType: 'Title',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.Title.PageTitle',
	},
	CPCertAgreementDetailsPageDescription: {
		defaultValue: '',
		description: 'Page description for: Cert Agreement Details.',
		displayPath: 'Credentials Agreement|Details|Description',
		portal: 'CP',
		displayType: 'Content',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: true,
		shared: false,
	},
	CPCertAgreementDetailsButtonBack: {
		defaultValue: 'Back',
		description: 'Button label for: Back.',
		displayPath: 'Credentials Agreement|Details|Back',
		portal: 'CP',
		displayType: 'Button',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.Button.Back',
	},
	CPCertAgreementDetailsButtonCancel: {
		defaultValue: 'Cancel',
		description: 'Button label for: Cancel.',
		displayPath: 'Credentials Agreement|Details|Cancel',
		portal: 'CP',
		displayType: 'Button',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.Button.Cancel',
	},
	CPCertAgreementDetailsButtonEdit: {
		defaultValue: 'Edit',
		description: 'Button label for: Edit.',
		displayPath: 'Credentials Agreement|Details|Edit',
		portal: 'CP',
		displayType: 'Button',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.Button.Edit',
	},
	CPCertAgreementDetailsButtonInfo: {
		defaultValue: 'Info',
		description: 'Button label for: Info.',
		displayPath: 'Credentials Agreement|Details|Info',
		portal: 'CP',
		displayType: 'Button',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName: 'Credential.Agreement/Credentials.Agreement.CertAgreement.Button.Info',
	},
	CPCertAgreementDetailsButtonUpdate: {
		defaultValue: 'Update',
		description: 'Button label for: Update.',
		displayPath: 'Credentials Agreement|Details|Update',
		portal: 'CP',
		displayType: 'Button',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.Button.Update',
	},
	CPCertAgreementDetailsFieldLabelActiveDate: {
		defaultValue: 'Active Date',
		description: 'Field label for: Active Date.',
		displayPath: 'Credentials Agreement|Details|ActiveDate',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.FieldLabel.ActiveDate',
	},
	CPCertAgreementDetailsFieldLabelDataProvider: {
		defaultValue: 'Data Provider',
		description: 'Field label for: Data Provider.',
		displayPath: 'Credentials Agreement|Details|DataProvider',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.FieldLabel.DataProvider',
	},
	CPCertAgreementDetailsFieldLabelDocumentation: {
		defaultValue: 'Documentation',
		description: 'Field label for: Documentation.',
		displayPath: 'Credentials Agreement|Details|Documentation',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.FieldLabel.Documentation',
	},
	CPCertAgreementDetailsFieldLabelDocumentationLanguage: {
		defaultValue: 'Documentation Language',
		description: 'Field label for: Documentation Language.',
		displayPath: 'Credentials Agreement|Details|DocLanguage',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.FieldLabel.DocumentationLanguage',
	},
	CPCertAgreementDetailsFieldLabelExpireDate: {
		defaultValue: 'Expire Date',
		description: 'Field label for: Expire Date.',
		displayPath: 'Credentials Agreement|Details|ExpireDate',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.FieldLabel.ExpireDate',
	},
	CPCertAgreementDetailsFieldLabelInvalid: {
		defaultValue: 'Invalid',
		description: 'Field label for: Invalid.',
		displayPath: 'Credentials Agreement|Details|Invalid',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.FieldLabel.Invalid',
	},
	CPCertAgreementDetailsFieldLabelNotes: {
		defaultValue: 'Notes',
		description: 'Field label for: Notes.',
		displayPath: 'Credentials Agreement|Details|Notes',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.FieldLabel.Notes',
	},
	CPCertAgreementDetailsFieldLabelProvider: {
		defaultValue: 'Provider',
		description: 'Field label for: Provider.',
		displayPath: 'Credentials Agreement|Details|Provider',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.FieldLabel.Provider',
	},
	CPCertAgreementDetailsFieldLabelReviewState: {
		defaultValue: 'Review Status',
		description: 'Field label for: Review State.',
		displayPath: 'Credentials Agreement|Details|ReviewState',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.OtherRequirement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.FieldLabel.ReviewState',
	},
	CPCertAgreementDetailsFieldLabelUpdated: {
		defaultValue: 'Updated',
		description: 'Field label for: Updated.',
		displayPath: 'Credentials Agreement|Details|Updated',
		portal: 'CP',
		displayType: 'Label',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.FieldLabel.Updated',
	},
	CPCertAgreementDetailsNotificationMessageUpdateSuccess: {
		defaultValue: 'Cert agreement updated successfully',
		description: 'Notification message for successful cert agreement update.',
		displayPath: 'Credentials Agreement|Details|UpdateSuccess',
		portal: 'CP',
		displayType: 'Notification',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
		legacyName:
			'Credential.Agreement/Credentials.Agreement.CertAgreement.Details.NotificationMessage.UpdateSuccess',
	},
	CPCertAgreementDetailsFieldErrorDocumentationRequired: {
		defaultValue: 'Documentation is required',
		description: 'Error message when documentation is required but not provided.',
		displayPath: 'Credentials Agreement|Details|Errors|DocumentationRequired',
		portal: 'CP',
		displayType: 'Error',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
	},
	CPCertAgreementDetailsFieldErrorDocumentationSize: {
		defaultValue: 'File size exceeds the maximum allowed limit',
		description: 'Error message when uploaded documentation file size is too large.',
		displayPath: 'Credentials Agreement|Details|Errors|DocumentationSize',
		portal: 'CP',
		displayType: 'Error',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
	},
	CPCertAgreementDetailsFieldErrorDocumentationLimit: {
		defaultValue: 'Maximum number of files exceeded',
		description: 'Error message when too many documentation files are uploaded.',
		displayPath: 'Credentials Agreement|Details|Errors|DocumentationLimit',
		portal: 'CP',
		displayType: 'Error',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
	},
	CPCertAgreementDetailsFieldErrorDocLanguageRequired: {
		defaultValue: 'Documentation language is required',
		description: 'Error message when documentation language is required but not selected.',
		displayPath: 'Credentials Agreement|Details|Errors|DocLanguageRequired',
		portal: 'CP',
		displayType: 'Error',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
	},
	CPCertAgreementDetailsFieldErrorDocLanguageInvalid: {
		defaultValue: 'Please select a valid documentation language',
		description: 'Error message when an invalid documentation language is selected.',
		displayPath: 'Credentials Agreement|Details|Errors|DocLanguageInvalid',
		portal: 'CP',
		displayType: 'Error',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
	},
	CPCertAgreementDetailsFieldErrorProviderRequired: {
		defaultValue: 'Provider is required',
		description: 'Error message when provider is required but not provided.',
		displayPath: 'Credentials Agreement|Details|Errors|ProviderRequired',
		portal: 'CP',
		displayType: 'Error',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: false,
	},
};

export const certAgreementDetailPageLocs: Pick<
	LocMap<CPCertAgreementDetailLocNames>,
	'CPCertAgreementDetailsPageTitle' | 'CPCertAgreementDetailsPageDescription'
> = {
	CPCertAgreementDetailsPageTitle: 'CPCertAgreementDetailsPageTitle',
	CPCertAgreementDetailsPageDescription: 'CPCertAgreementDetailsPageDescription',
};

export const cpCertAgreementDetailLocKeys =
	MapLocKeys<CPCertAgreementDetailLocNames>(cpCertAgreementDetailLocs);

export type CPCertAgreementDetailLocKeys = typeof cpCertAgreementDetailLocKeys;
